// src/components/Inventory/Warehouses/ProductTransactions/AddProductMovementTransaction.js

import React, { useState, useEffect, useContext } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { UserContext } from "../../../../../UserContext";
import toast from "react-hot-toast";
import {
  FaArrowLeft,
  FaPlus,
  FaLeaf,
  FaExchangeAlt,
  FaSearch,
  FaTimes,
  FaCalendarAlt,
  FaBox,
  FaEllipsisH,
} from "react-icons/fa";
import "./style.css";

// Movement types matching updated Android implementation - 4 types
const MOVEMENT_TYPES = {
  LOAD: {
    value: 1,
    label: "Load",
    labelAr: "إضافة",
    apiValue: "load",
    icon: <FaPlus />,
  },
  USAGE: {
    value: 2,
    label: "Usage",
    labelAr: "استخدام",
    apiValue: "usage",
    icon: <FaLeaf />,
  },
  ALLOCATE_PRODUCT: {
    value: 3,
    label: "Allocate Product",
    labelAr: "نقل المنتج",
    apiValue: "allocate",
    icon: <FaExchangeAlt />,
  },
  OTHER: {
    value: 4,
    label: "Other",
    labelAr: "أخرى",
    apiValue: "other",
    icon: <FaEllipsisH />,
  },
};

const AddProductMovementTransaction = () => {
  const { productId, warehouseId: warehouseIdParam } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { token, userData } = useContext(UserContext);
  
  // Get warehouseId from location state or params
  const warehouseId = location.state?.warehouseId || warehouseIdParam;

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [productSearchOpen, setProductSearchOpen] = useState(false);
  const [productSearchQuery, setProductSearchQuery] = useState("");
  const [selectedMovementType, setSelectedMovementType] = useState(null);
  const [currentWarehouse, setCurrentWarehouse] = useState(null);
  const [increaseProduct, setIncreaseProduct] = useState(true); // For OTHER type radio buttons

  const [formData, setFormData] = useState({
    movementType: "",
    quantity: "",
    price: "",
    date: new Date().toISOString().split("T")[0],
    sourceLocationId: "",
    destinationLocationId: "",
    fieldId: "",
    notes: "",
  });

  // State for dropdown options
  const [warehouses, setWarehouses] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});

  // Get farm type from user data
  const farmType =
    userData?.Employee?.Farm?.type || userData?.Farm?.type || "soil";

  // Fetch initial data with retry mechanism
  useEffect(() => {
    const fetchInitialData = async (retryCount = 0) => {
      if (!token || !userData.Employee?.farmId) return;
      const farmId = userData.Employee.farmId;
      
      // Only show loading on first attempt
      if (retryCount === 0) {
        setLoading(true);
      }

      try {
        const promises = [
          axios.get(`api/inventoryLocation/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`api/field/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
          axios.get(`api/inventoryItemDetails/findByFarmId/${farmId}`, {
            headers: { Authorization: token },
          }),
        ];

        // If productId provided, fetch that specific product
        if (productId) {
          promises.push(
            axios.get(`api/inventoryItemDetails/${productId}`, {
              headers: { Authorization: token },
            })
          );
        }

        // If warehouseId provided, fetch that specific warehouse
        if (warehouseId) {
          promises.push(
            axios.get(`api/inventoryLocation/${warehouseId}`, {
              headers: { Authorization: token },
            })
          );
        }

        const responses = await Promise.all(promises);

        const warehousesList = responses[0].data.items || [];
        const fieldsList = responses[1].data.items || [];
        const productsList = responses[2].data.items || [];
        
        // Retry logic for eventually consistent data
        if (retryCount < 3) {
          const shouldRetry = 
            (warehousesList.length === 0 && retryCount === 0) ||
            (productsList.length === 0 && retryCount === 0);
          
          if (shouldRetry) {
            const delay = Math.min(1000 * Math.pow(2, retryCount), 3000);
            console.log(`Retrying data fetch in ${delay}ms (attempt ${retryCount + 1}/3)`);
            setTimeout(() => fetchInitialData(retryCount + 1), delay);
            return;
          }
        }
        
        setWarehouses(warehousesList);
        setFields(fieldsList);
        setAllProducts(productsList);

        // Set initial product if productId was provided
        if (productId && responses[3]) {
          const fetchedProduct = responses[3].data.items[0];
          setSelectedProduct(fetchedProduct);
          await fetchProductInventoryLocations(
            fetchedProduct.id,
            warehousesList
          );
        }

        // Set current warehouse - prioritize warehouseId parameter over product's original location
        if (warehouseId && responses[4]) {
          // We have a specific warehouse ID (e.g., for transferred products)
          const fetchedWarehouse = responses[4].data.items[0];
          setCurrentWarehouse(fetchedWarehouse);
          setFormData((prev) => ({
            ...prev,
            sourceLocationId: fetchedWarehouse.id,
          }));
        } else if (warehouseId && !responses[4]) {
          // Try to find warehouse from the list if fetch failed
          const warehouse = warehousesList.find(
            (w) => w.id === parseInt(warehouseId)
          );
          if (warehouse) {
            setCurrentWarehouse(warehouse);
            setFormData((prev) => ({
              ...prev,
              sourceLocationId: warehouse.id,
            }));
          }
        } else if (productId && responses[3]) {
          // Fallback to product's original location only if no warehouseId provided
          const fetchedProduct = responses[3].data.items[0];
          if (fetchedProduct.locationId) {
            setFormData((prev) => ({
              ...prev,
              sourceLocationId: fetchedProduct.locationId,
            }));
            const warehouse = warehousesList.find(
              (w) => w.id === fetchedProduct.locationId
            );
            if (warehouse) setCurrentWarehouse(warehouse);
          }
        }
      } catch (error) {
        console.error("Failed to fetch initial data:", error);
        
        // Retry on network errors
        if (retryCount < 2 && (error.code === 'ECONNABORTED' || !error.response)) {
          const delay = 1000 * (retryCount + 1);
          console.log(`Network error, retrying in ${delay}ms...`);
          setTimeout(() => fetchInitialData(retryCount + 1), delay);
          return;
        }
        
        toast.error(t("Failed to load data."));
      } finally {
        if (retryCount === 0 || retryCount >= 2) {
          setLoading(false);
        }
      }
    };

    // Inner function for fetching product inventory
    const fetchProductInventoryLocations = async (
      productId,
      warehousesList
    ) => {
      if (!productId) return;

      // Source locations are now handled via currentWarehouse
    };

    fetchInitialData();
  }, [productId, warehouseId, token, userData, t]);

  // Fetch product-specific inventory locations
  const fetchProductInventoryLocations = async (productId) => {
    if (!productId || !token || !userData.Employee?.farmId) return;

    // Source locations are now handled via currentWarehouse
    // This function is kept for potential future use
  };

  // Handle product selection
  const handleProductSelect = async (product) => {
    setSelectedProduct(product);
    setProductSearchOpen(false);
    setProductSearchQuery("");

    await fetchProductInventoryLocations(product.id);

    // Don't reset source if it's the current warehouse
    if (
      !currentWarehouse ||
      formData.sourceLocationId !== currentWarehouse.id
    ) {
      setFormData((prev) => ({
        ...prev,
        sourceLocationId: product.locationId || "",
        destinationLocationId: "",
      }));
    }
  };

  // Handle movement type change - matching Android logic
  const handleMovementTypeChange = (movementType) => {
    setSelectedMovementType(movementType);
    setFormData((prev) => ({
      ...prev,
      movementType: movementType.value,
      // Reset conditional fields
      fieldId: "",
      destinationLocationId: "",
      notes:
        movementType.value !== MOVEMENT_TYPES.OTHER.value ? "" : prev.notes,
      price: "",
    }));

    // Reset increase/decrease for OTHER type
    if (movementType.value === MOVEMENT_TYPES.OTHER.value) {
      setIncreaseProduct(true); // Default to increase
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // Validate form based on movement type
  const validateForm = () => {
    const errors = {};
    const quantityText = formData.quantity.toString().trim();

    if (!selectedProduct) {
      errors.product = t("Product is required");
    }

    if (!selectedMovementType) {
      errors.movementType = t("Movement type is required");
    }

    if (!quantityText || parseFloat(quantityText) <= 0) {
      errors.quantity = t("Valid quantity is required");
    }

    if (!formData.date) {
      errors.date = t("Date is required");
    }

    if (!formData.sourceLocationId) {
      errors.sourceLocationId = t("Source location is required");
    }

    // Movement type specific validations
    if (selectedMovementType?.value === MOVEMENT_TYPES.ALLOCATE_PRODUCT.value) {
      if (!formData.destinationLocationId) {
        errors.destinationLocationId = t("Destination location is required");
      }
      if (formData.sourceLocationId === formData.destinationLocationId) {
        errors.destinationLocationId = t(
          "Source and destination must be different"
        );
      }
    }

    // Field required for USAGE in soil farms only
    if (
      selectedMovementType?.value === MOVEMENT_TYPES.USAGE.value &&
      farmType === "soil" &&
      !formData.fieldId
    ) {
      errors.fieldId = t("Field is required for usage");
    }

    // Notes required for OTHER type
    if (
      selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value &&
      !formData.notes
    ) {
      errors.notes = t("Notes are required for other movement type");
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Helper function to combine selected date with current time
  const getTimestampWithCurrentTime = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const now = new Date();
    now.setFullYear(parseInt(year));
    now.setMonth(parseInt(month) - 1); // Month is 0-indexed
    now.setDate(parseInt(day));
    return now.getTime();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error(t("Please fill all required fields"));
      return;
    }

    const {
      movementType,
      quantity,
      date,
      notes,
      price,
      sourceLocationId,
      destinationLocationId,
      fieldId,
    } = formData;

    const basePayload = {
      tenantId: userData.Tenant.id,
      farmId: userData.Employee.farmId,
      inventoryItemDetailId: parseInt(selectedProduct.id),
      quantity: parseFloat(quantity),
      movementNotes: notes || "",
    };

    let request;

    // Handle different movement types based on Android logic
    if (movementType === MOVEMENT_TYPES.LOAD.value) {
      // LOAD - use procurement endpoint
      request = axios.post(
        "api/inventoryProcurement",
        {
          ...basePayload,
          locationId: parseInt(sourceLocationId),
          procurementDate: getTimestampWithCurrentTime(date),
          movementType: "load",
          supplierId: null,
          price: price ? parseFloat(price) : null,
        },
        { headers: { Authorization: token } }
      );
    } else if (movementType === MOVEMENT_TYPES.USAGE.value) {
      // USAGE - use distribution endpoint
      request = axios.post(
        "api/inventoryDistribution",
        {
          ...basePayload,
          locationId: parseInt(sourceLocationId),
          distributionDate: getTimestampWithCurrentTime(date),
          movementType: "usage",
          clientId: null,
          fieldId: fieldId ? parseInt(fieldId) : null,
        },
        { headers: { Authorization: token } }
      );
    } else if (movementType === MOVEMENT_TYPES.ALLOCATE_PRODUCT.value) {
      // ALLOCATE_PRODUCT - use move endpoint
      request = axios.post(
        "api/inventoryLocation/move",
        {
          sourceLocation: parseInt(sourceLocationId),
          destinationLocation: parseInt(destinationLocationId),
          itemId: parseInt(selectedProduct.id),
          quantity: parseInt(quantity),
        },
        { headers: { Authorization: token } }
      );
    } else if (movementType === MOVEMENT_TYPES.OTHER.value) {
      // OTHER type - determine endpoint based on increase/decrease
      if (increaseProduct) {
        // Increase - use procurement
        request = axios.post(
          "api/inventoryProcurement",
          {
            ...basePayload,
            locationId: parseInt(sourceLocationId),
            procurementDate: getTimestampWithCurrentTime(date),
            movementType: "other",
            supplierId: null,
            price: price ? parseFloat(price) : null,
          },
          { headers: { Authorization: token } }
        );
      } else {
        // Decrease - use distribution
        request = axios.post(
          "api/inventoryDistribution",
          {
            ...basePayload,
            locationId: parseInt(sourceLocationId),
            distributionDate: getTimestampWithCurrentTime(date),
            movementType: "other",
            clientId: null,
            fieldId: null,
          },
          { headers: { Authorization: token } }
        );
      }
    }

    try {
      await toast.promise(request, {
        loading: t("Saving movement..."),
        success: t("Movement saved successfully!"),
        error: (err) => {
          // Same as Android: Display the backend error message directly
          const message =
            err.response?.data?.message || t("Failed to save movement.");
          return message;
        },
      });

      // Navigate back with refresh flag, preserving warehouseId if present
      if (productId) {
        navigate(`/inventory/product/${productId}/transactions`, {
          state: {
            refresh: true,
            warehouseId: warehouseId ? parseInt(warehouseId) : undefined
          }
        });
      } else {
        navigate(-1);
      }
    } catch (error) {
      console.error("Submission error:", error);
    }
  };

  const getDisplayName = (item) => {
    if (!item) return "";
    return i18n.language === "ar"
      ? item.nameAr || item.arabicName || item.name
      : item.nameEn ||
          item.englishName ||
          item.name ||
          item.nameAr ||
          item.arabicName;
  };

  const getMovementTypeLabel = (type) => {
    if (!type) return "";
    return i18n.language === "ar" ? type.labelAr : type.label;
  };

  // Filter products for search
  const filteredProducts = allProducts.filter((p) => {
    const name = getDisplayName(p).toLowerCase();
    return name.includes(productSearchQuery.toLowerCase());
  });

  // Determine if price field should be visible
  const isPriceFieldVisible = () => {
    return (
      selectedMovementType?.value === MOVEMENT_TYPES.LOAD.value ||
      (selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value &&
        increaseProduct)
    );
  };

  // Determine if notes field should be visible
  const isNotesFieldVisible = () => {
    return selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value;
  };

  if (loading) {
    return (
      <div className="add-transaction-container">
        <div className="loading-spinner">{t("Loading...")}</div>
      </div>
    );
  }

  return (
    <div className="add-transaction-container">
      <header className="view-header">
        <div className="header-left-content">
          <button
            className="button-icon-only"
            onClick={() => navigate(-1)}
            aria-label={t("Go back")}
          >
            <FaArrowLeft />
          </button>
          <div>
            <h1 className="view-header-title">{t("Add Product Movement")}</h1>
            {selectedProduct && (
              <p className="view-header-subtitle">
                {getDisplayName(selectedProduct)}
              </p>
            )}
          </div>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="transaction-form-card">
        {/* Product Selection Field */}
        <div
          className={`form-group ${validationErrors.product ? "error" : ""}`}
        >
          <label htmlFor="product">
            {t("Product")} <span className="required">*</span>
          </label>
          <div className="product-select-wrapper">
            <input
              type="text"
              id="product"
              value={selectedProduct ? getDisplayName(selectedProduct) : ""}
              onClick={() => !productId && setProductSearchOpen(true)}
              readOnly
              placeholder={t("Select Product")}
              className="product-select-input"
              disabled={!!productId}
            />
            {!productId && (
              <button
                type="button"
                className="product-search-btn"
                onClick={() => setProductSearchOpen(true)}
              >
                <FaSearch />
              </button>
            )}
          </div>
          {validationErrors.product && (
            <span className="error-message">{validationErrors.product}</span>
          )}
        </div>

        {/* Movement Type Selection - 4 types matching Android */}
        <div
          className={`form-group ${
            validationErrors.movementType ? "error" : ""
          }`}
        >
          <label htmlFor="movementType">
            {t("Movement Type")} <span className="required">*</span>
          </label>
          <select
            id="movementType"
            name="movementType"
            value={formData.movementType}
            onChange={(e) => {
              const selected = Object.values(MOVEMENT_TYPES).find(
                (m) => m.value === parseInt(e.target.value)
              );
              if (selected) handleMovementTypeChange(selected);
            }}
            className="movement-type-select"
          >
            <option value="">{t("Select Movement Type")}</option>
            {Object.values(MOVEMENT_TYPES).map((type) => (
              <option key={type.value} value={type.value}>
                {getMovementTypeLabel(type)}
              </option>
            ))}
          </select>
          {validationErrors.movementType && (
            <span className="error-message">
              {validationErrors.movementType}
            </span>
          )}
        </div>

        {/* Other Type Radio Buttons - Increase/Decrease */}
        {selectedMovementType?.value === MOVEMENT_TYPES.OTHER.value && (
          <div className="form-group">
            <label>{t("Movement Type")}</label>
            <div className="radio-button-group">
              <label className="radio-button-label">
                <input
                  type="radio"
                  name="increaseDecrease"
                  checked={increaseProduct}
                  onChange={() => setIncreaseProduct(true)}
                />
                <span className="radio-button-text">
                  {t("Increased Quantity")}
                </span>
              </label>
              <label className="radio-button-label">
                <input
                  type="radio"
                  name="increaseDecrease"
                  checked={!increaseProduct}
                  onChange={() => setIncreaseProduct(false)}
                />
                <span className="radio-button-text">
                  {t("Decreased Quantity")}
                </span>
              </label>
            </div>
          </div>
        )}

        {/* Source Location - Always shows current warehouse */}
        <div className={`form-group`}>
          <label htmlFor="sourceLocationId">{t("From Warehouse")}</label>
          <input
            type="text"
            id="sourceLocationId"
            value={
              currentWarehouse
                ? getDisplayName(currentWarehouse)
                : t("No warehouse selected")
            }
            disabled
            className="disabled-input"
          />
        </div>

        {/* Destination Location - Only for ALLOCATE_PRODUCT */}
        {selectedMovementType?.value ===
          MOVEMENT_TYPES.ALLOCATE_PRODUCT.value && (
          <div
            className={`form-group ${
              validationErrors.destinationLocationId ? "error" : ""
            }`}
          >
            <label htmlFor="destinationLocationId">
              {t("Destination")} <span className="required">*</span>
            </label>
            <select
              id="destinationLocationId"
              name="destinationLocationId"
              value={formData.destinationLocationId}
              onChange={handleChange}
              disabled={!selectedProduct}
            >
              <option value="">
                {!selectedProduct
                  ? t("Select product first")
                  : t("Select Destination")}
              </option>
              {warehouses
                .filter((w) => w.id !== parseInt(formData.sourceLocationId))
                .map((w) => (
                  <option key={w.id} value={w.id}>
                    {getDisplayName(w)}
                  </option>
                ))}
            </select>
            {validationErrors.destinationLocationId && (
              <span className="error-message">
                {validationErrors.destinationLocationId}
              </span>
            )}
          </div>
        )}

        {/* Field Selection - Only for USAGE in SOIL farms */}
        {selectedMovementType?.value === MOVEMENT_TYPES.USAGE.value &&
          farmType === "soil" && (
            <div
              className={`form-group ${
                validationErrors.fieldId ? "error" : ""
              }`}
            >
              <label htmlFor="fieldId">
                {t("Field")} <span className="required">*</span>
              </label>
              <select
                id="fieldId"
                name="fieldId"
                value={formData.fieldId}
                onChange={handleChange}
              >
                <option value="">{t("Select Field")}</option>
                {fields.map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </select>
              {validationErrors.fieldId && (
                <span className="error-message">
                  {validationErrors.fieldId}
                </span>
              )}
            </div>
          )}

        {/* Quantity Field */}
        <div
          className={`form-group ${validationErrors.quantity ? "error" : ""}`}
        >
          <label htmlFor="quantity">
            {t("Quantity")} <span className="required">*</span>
          </label>
          <input
            type="number"
            step="any"
            id="quantity"
            name="quantity"
            value={formData.quantity}
            onChange={handleChange}
            placeholder={t("0.00")}
          />
          {validationErrors.quantity && (
            <span className="error-message">{validationErrors.quantity}</span>
          )}
        </div>

        {/* Notes Field - Only for OTHER type (required) */}
        {isNotesFieldVisible() && (
          <div
            className={`form-group ${validationErrors.notes ? "error" : ""}`}
          >
            <label htmlFor="notes">
              {t("Notes")} <span className="required">*</span>
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows="4"
              placeholder={t("Enter notes...")}
            ></textarea>
            {validationErrors.notes && (
              <span className="error-message">{validationErrors.notes}</span>
            )}
          </div>
        )}

        {/* Price Field - For LOAD and OTHER (increase) */}
        {isPriceFieldVisible() && (
          <div className="form-group">
            <label htmlFor="price">{t("Price")}</label>
            <input
              type="number"
              step="any"
              id="price"
              name="price"
              value={formData.price}
              onChange={handleChange}
              placeholder={t("0.00")}
            />
          </div>
        )}

        {/* Date Field */}
        <div className={`form-group ${validationErrors.date ? "error" : ""}`}>
          <label htmlFor="date">
            {t("Date")} <span className="required">*</span>
          </label>
          <div className="date-input-wrapper">
            <input
              type="date"
              id="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              max={new Date().toISOString().split("T")[0]}
            />
            <FaCalendarAlt className="date-icon" />
          </div>
          {validationErrors.date && (
            <span className="error-message">{validationErrors.date}</span>
          )}
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button
            type="button"
            className="button-secondary"
            onClick={() => navigate(-1)}
          >
            {t("Cancel")}
          </button>
          <button type="submit" className="button-primary">
            {t("Save")}
          </button>
        </div>
      </form>

      {/* Product Search Modal */}
      {productSearchOpen && (
        <div
          className="modal-overlay"
          onClick={() => setProductSearchOpen(false)}
        >
          <div
            className="modal-content product-search-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{t("Select Product")}</h2>
              <button
                className="modal-close"
                onClick={() => setProductSearchOpen(false)}
              >
                <FaTimes />
              </button>
            </div>

            <div className="modal-search">
              <input
                type="text"
                placeholder={t("Search products...")}
                value={productSearchQuery}
                onChange={(e) => setProductSearchQuery(e.target.value)}
                autoFocus
              />
            </div>

            <div className="products-list">
              {filteredProducts.length === 0 ? (
                <div className="no-products">{t("No products found")}</div>
              ) : (
                filteredProducts.map((product) => (
                  <div
                    key={product.id}
                    className="product-item"
                    onClick={() => handleProductSelect(product)}
                  >
                    <div className="product-item-content">
                      <FaBox className="product-icon" />
                      <div className="product-details">
                        <div className="product-name">
                          {getDisplayName(product)}
                        </div>
                        {product.unit && (
                          <div className="product-unit">{product.unit}</div>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddProductMovementTransaction;
